import { useEffect, useState } from "react";
import { History, Truck, CalendarCheck, Loader2, PackageCheck, PackageX, Ban } from "lucide-react";
import { orderClientHistory } from "../../../../../api/auth";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
};

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
      <Icon className={`h-4 w-4 ${tone}`} />
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</div>
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{value}</div>
      </div>
    </div>
  );
}

/**
 * Historique du client DANS CE STORE uniquement (endpoint client-history).
 * Aucune donnée des autres stores n'est demandée ni affichée.
 */
export function ClientHistoryPanel({ orderId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, data: null });
    orderClientHistory(orderId)
      .then((res) => alive && setState({ loading: false, error: null, data: res.data }))
      .catch((err) =>
        alive &&
        setState({
          loading: false,
          error: err?.response?.data?.detail || "Historique indisponible.",
          data: null,
        })
      );
    return () => {
      alive = false;
    };
  }, [orderId]);

  const { loading, error, data } = state;
  const last = data?.last_delivered;
  const totals = data?.totals || {};

  return (
    <section className="surface-card rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          <History className="h-4 w-4 text-indigo-600" />
          Historique du client dans votre boutique
        </h3>
        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Ce store uniquement</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement de l'historique…
        </div>
      ) : error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : !data?.has_history ? (
        <p className="empty-state rounded-lg bg-slate-50 p-3 text-sm text-slate-500 dark:bg-slate-800">
          Première commande de ce client dans votre boutique.
        </p>
      ) : (
        <div className="space-y-3">
          {last ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-500/30 dark:bg-emerald-500/10">
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-emerald-700 dark:text-emerald-400">
                Dernière commande livrée
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
                  <div>
                    <div className="text-xs text-slate-500">Livrée par</div>
                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
                      {last.delivery_agency_name || "Transporteur inconnu"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarCheck className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
                  <div>
                    <div className="text-xs text-slate-500">Date de livraison</div>
                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{formatDate(last.delivered_at)}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Commande</div>
                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100" title={last.produit}>
                    {last.produit || `#${last.external_order_id}`}
                  </div>
                  <div className="text-xs text-slate-500">
                    {last.total_amount} {last.currency}
                    {last.delivery_tracking_id ? ` · ${last.delivery_tracking_id}` : ""}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
              Ce client a déjà commandé chez vous mais aucune commande n'a encore été livrée.
            </p>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat icon={History} label="Commandes" value={totals.orders ?? 0} tone="text-indigo-600" />
            <Stat icon={PackageCheck} label="Livrées" value={totals.delivered ?? 0} tone="text-emerald-600" />
            <Stat icon={PackageX} label="Retournées" value={totals.returned ?? 0} tone="text-rose-600" />
            <Stat icon={Ban} label="Annulées" value={totals.cancelled ?? 0} tone="text-amber-600" />
          </div>
        </div>
      )}
    </section>
  );
}
